import React, {ReactElement, ReactNode, useEffect} from 'react';
import merge from 'lodash/merge';
import {observer} from 'mobx-react-lite';
import {useService} from '@yosmanyga/di';
import {Button, Container, I18NText, Icon, Input, Text, useIcons,} from '@yosmanyga/rich-ui';
import {ThemeBuildType, ThemeProvider, ThemeVariablesType} from '@yosmanyga/theme';
import {FeedbackController} from '../../controller';

const Feedback = observer(({
    templates,
    onSend
}: FeedbackProps) => {
    const icons = useIcons();

    const controller = useService(FeedbackController, []);

    useEffect(() => {
        (async () => {
            await controller.load();
        })();
    }, []);

    useEffect(() => {
        onSend?.();
    }, []);

    const InputComponent = observer(() => {
        return <Container>
            <I18NText>Contact our team</I18NText>
            <Container
                flow="row"
                align={{
                    cross: 'center',
                }}
            >
                <Input
                    flex={{
                        grow: 1,
                        shrink: 1,
                    }}
                    value={controller.input}
                    onChange={(value) => {
                        controller.prepare(value);
                    }}
                    onEnter={async () => {
                        await controller.send();

                        onSend?.();
                    }}
                    capitalize="sentences"
                    focus
                />
                <Button
                    progress={controller.sending}
                    disabled={!controller.input}
                    margin={{
                        left: 1,
                    }}
                    onClick={async () => {
                        await controller.send();

                        onSend?.();
                    }}
                >
                    <Icon
                        component={icons.actions.send}
                        size='xs'
                    />
                </Button>
            </Container>
        </Container>
    });

    const StoryComponent = observer(() => {
        if (!controller.loaded) {
            return null;
        }

        return <>
            {controller.indexes.map(({user, message}, i) => {
                const content = controller.contents.find(
                    ({id}) => id === message
                );

                if (!content) {
                    throw new Error();
                }

                return <Text
                    key={i}
                    intent={user === 'sys' ? 'sys_message' : 'user_message'}
                >
                    {content.text}
                </Text>
            })}
        </>
    })

    return <templates.layout
        story={<StoryComponent/>}
        input={<InputComponent/>}
    />
});

type FeedbackProps = {
    templates: {
        layout: (
            params: {
                story?: ReactElement;
                input?: ReactElement;
            }
        ) => ReactNode;
    };
    onSend?: () => void;
};

const FeedbackWithTheme = (props: FeedbackProps) => {
    return <ThemeProvider
        build={(variables?: ThemeVariablesType, prev?: ThemeBuildType) => {
            const {palette} = variables as ThemeVariablesType;

            const local = {
                text: {
                    sys_message_intent: {
                        align: {
                            self: 'flex-end',
                        },
                    },
                    user_message_intent: {
                        //color: palette.primary.left,
                    },
                },
            };

            return merge(
                {},
                prev && prev(variables),
                local
            );
        }}
        variables={undefined}
    >
        <Feedback {...props} />
    </ThemeProvider>
};

export type {
    FeedbackProps,
}

export {
    FeedbackWithTheme as Feedback
};
